import { useEffect, useMemo } from 'react';

import { useQuery } from '@apollo/client/react';
import {
  Alert,
  type SxProps,
  type Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { EMAIL_LIMIT_PER_ROLLING_24H } from '@realadvisor/constants';

import { gql } from '@graphql/__generated__';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { useLocale } from '@shared/hooks/locale';

const SENT_EMAIL_COUNT_OVER_ROLLING_24H_QUERY = gql(/* GraphQL */ `
  query SentEmailCountOverRolling24H($userId: uuid!, $date: timestamptz!) {
    email_messages_aggregate(
      where: {
        created_by: { _eq: $userId }
        is_sent: { _eq: true }
        status: { _eq: "sent" }
        date: { _gte: $date }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`);

export const EmailCounterAlert = ({
  onCount,
  sx,
  variant = 'alert',
}: {
  onCount?: (limitReached: boolean) => void;
  sx?: SxProps<Theme>;
  // 'compact': a caption for tight footers ("count / limit", or the limit
  // message once it is reached).
  variant?: 'alert' | 'compact';
}) => {
  const { t } = useLocale();
  const { me } = useAppData();

  const nowMinus24Hours = useMemo(() => {
    return new Date(Date.now() - 24 * 60 * 60 * 1000);
  }, []);

  const { data } = useQuery(SENT_EMAIL_COUNT_OVER_ROLLING_24H_QUERY, {
    variables: {
      userId: me?.id ?? '',
      date: nowMinus24Hours.toISOString(),
    },
    skip: me?.id == null,
  });

  const count = data?.email_messages_aggregate?.aggregate?.count ?? 0;
  const limitReached = count >= EMAIL_LIMIT_PER_ROLLING_24H;

  useEffect(() => {
    if (onCount) {
      onCount(limitReached);
    }
  }, [limitReached, onCount]);

  const limitReachedText = t(
    'You have reached the limit of emails ({{limit}}) you can send in a rolling 24 hours',
    {
      limit: EMAIL_LIMIT_PER_ROLLING_24H,
    },
  );
  const sentCountText = t(
    'You have sent {{count}} / {{limit}} emails in the last rolling 24 hours',
    {
      count,
      limit: EMAIL_LIMIT_PER_ROLLING_24H,
    },
  );
  const sxList = Array.isArray(sx) ? sx : [sx];

  if (variant === 'compact') {
    return limitReached ? (
      <Typography variant="caption" sx={[{ color: 'error.main' }, ...sxList]}>
        {limitReachedText}
      </Typography>
    ) : (
      <Tooltip title={sentCountText}>
        <Typography
          variant="caption"
          sx={[{ color: 'text.secondary' }, ...sxList]}
        >
          {`${count} / ${EMAIL_LIMIT_PER_ROLLING_24H}`}
        </Typography>
      </Tooltip>
    );
  }

  return (
    <Alert
      sx={[{ marginX: 3 }, ...sxList]}
      severity={limitReached ? 'error' : 'info'}
    >
      {limitReached ? limitReachedText : sentCountText}
    </Alert>
  );
};
